import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import unifiedApi from '../services/unifiedApi';

export const useHolidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // form state for create/edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // null = create, object = edit
  const HOLIDAY_TYPES = [
    { value: 'school_vacation', label: 'Vacances scolaires' },
    { value: 'public_holiday', label: 'Jour férié national' },
    { value: 'exceptional', label: 'Fermeture exceptionnelle' },
  ];

  const [form, setForm] = useState({
    label: '',
    type: 'school_vacation',
    start_date: '',
    end_date: '',
  });

  const loadHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const res = await unifiedApi.holidays.getAll();
      if (res.success) setHolidays(res.data);
      else toast.error('Impossible de charger les vacances');
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  const openCreate = () => {
    setEditTarget(null);
    setForm({
      label: '',
      type: 'school_vacation',
      start_date: '',
      end_date: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (holiday) => {
    setEditTarget(holiday);
    setForm({
      label: holiday.label,
      type: holiday.type || 'school_vacation',
      start_date: holiday.start_date,
      end_date: holiday.end_date,
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditTarget(null);
    setForm({
      label: '',
      type: 'school_vacation',
      start_date: '',
      end_date: '',
    });
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.label.trim() || !form.start_date || !form.end_date) {
      toast.error('Tous les champs sont requis');
      return;
    }
    if (form.end_date < form.start_date) {
      toast.error('La date de fin doit être après la date de début');
      return;
    }

    setSubmitting(true);
    try {
      let res;
      if (editTarget) {
        res = await unifiedApi.holidays.update(editTarget.id, form);
      } else {
        res = await unifiedApi.holidays.create(form);
      }

      if (res.success) {
        toast.success(editTarget ? 'Période modifiée' : 'Période créée');
        closeDialog();
        loadHolidays();
      } else {
        toast.error(res.message || 'Erreur lors de la sauvegarde');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await unifiedApi.holidays.delete(id);
      if (res.success) {
        toast.success('Période supprimée');
        setHolidays((prev) => prev.filter((h) => h.id !== id));
      } else {
        toast.error(res.message || 'Erreur lors de la suppression');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const isCurrentlyOnHoliday = () => {
    const today = new Date().toISOString().split('T')[0];
    return holidays.some((h) => h.start_date <= today && today <= h.end_date);
  };

  return {
    holidays,
    loading,
    submitting,
    dialogOpen,
    editTarget,
    form,
    HOLIDAY_TYPES,
    openCreate,
    openEdit,
    closeDialog,
    handleFormChange,
    handleSubmit,
    handleDelete,
    isCurrentlyOnHoliday,
    loadHolidays,
  };
};
